import { z } from 'zod';

// ステータスは年度横断で同じセット (entered → checkedByAdmin → waitingForPay → paid)。
export const exhibitorStatusSchema = z.enum([
  'entered',
  'checkedByAdmin',
  'waitingList',
  'waitingForPay',
  'paid',
  'cancelled',
]);

// 出展タイプ。venue_fee_tier の主たる driver。
export const exhibitorTypeSchema = z.enum([
  'general',
  'student-university',
  'student-high-school',
  'corporate',
]);

export const exhibitDaySchema = z.enum(['saturday', 'sunday']);

export const boothSizePreferenceSchema = z.enum([
  'full-only', // 0.5ブースにしてほしくない (1ブースを強く希望)
  'prefer-full', // 0.5ブースでもよいが、できれば 1ブース希望
  'half-ok', // 0.5ブースでよい
  'half-prefer', // 0.5ブースを積極的に希望
]);

// 全年度共通の申込項目 (DB 上は exhibitors テーブルのトップレベル列に対応)。
// 年度固有 (机椅子セット, 電源容量 dropdown, table_count int, 0.5 ブース希望 等) は別ファイルへ。
export const exhibitorCoreSchema = z.object({
  // realName: 本名。運営からの連絡用。一般公開はされない (機密情報扱い)。
  realName: z.string().min(1, '本名は必須です'),
  email: z.string().optional(), // アカウントのメールを使うため optional
  // exhibitorName: 出展者名。公式サイト・会場掲示で使う表示名。HN / 団体名 / 本名どれでも可。
  exhibitorName: z.string().min(1, '出展者名は必須です'),
  phone: z
    .string()
    .min(1, '電話番号は必須です')
    .regex(/^0\d{9,10}$/, '電話番号は 0 から始まる 10〜11 桁の数字のみで入力してください (ハイフン不要)'),
  // http(s) スキームのみ許可。javascript:/data: 等は new URL() が成功するため
  // z.string().url() を通過してしまう。href 描画時の DOM-based XSS を防ぐべく明示的に弾く。
  website: z
    .string()
    .url()
    .refine((u) => /^https?:\/\//i.test(u), 'http(s):// で始まる URL を入力してください')
    .optional()
    .or(z.literal('')),
  twitterId: z.string().optional(),
  exhibitTitle: z.string().min(1, '出展タイトルは必須です'),
  exhibitDescription: z.string().min(10, '出展内容は10文字以上で入力してください'),
  images: z.array(z.string().url()).optional(),
  type: exhibitorTypeSchema,
  // 旧 boothRequirements は廃止。要望は specialRequests に統合する方針 (2026)。
  specialRequests: z.string().optional(),
  exhibitDays: z.array(exhibitDaySchema).min(1, '出展日は最低1日選択してください'),
  deliveryMethods: z.array(z.enum(['shipping', 'car'])).optional().nullable(),
  // 同意系。zod refine で submit 時に必ず true を要求しつつ、DB にも boolean で
  // 焼き付ける (将来規約改訂後の同意更新を識別できるよう監査用に残す)。
  agreedToTerms: z.boolean().refine((v) => v === true, '利用規約への同意が必要です'),
  agreedToSafetyGuidelines: z.boolean().refine((v) => v === true, '安全ガイドラインへの同意が必要です'),
});

// admin 用の最小ステータス更新 (年度横断)。
export const exhibitorAdminUpdateSchema = z.object({
  id: z.string(),
  status: exhibitorStatusSchema.optional(),
  reviewedBy: z.string().optional(),
  boothNumber: z.string().optional(),
  adminNotes: z.string().optional(),
});

// 出展者自身が編集できるフィールド (年度横断で同一)。
export const exhibitorEditableSchema = exhibitorCoreSchema.pick({
  exhibitTitle: true,
  exhibitDescription: true,
  exhibitorName: true,
  website: true,
  twitterId: true,
  images: true,
});

export type ExhibitorStatus = z.infer<typeof exhibitorStatusSchema>;
export type ExhibitorType = z.infer<typeof exhibitorTypeSchema>;
export type ExhibitDay = z.infer<typeof exhibitDaySchema>;
export type BoothSizePreference = z.infer<typeof boothSizePreferenceSchema>;
export type ExhibitorCoreInput = z.infer<typeof exhibitorCoreSchema>;
export type ExhibitorAdminUpdateInput = z.infer<typeof exhibitorAdminUpdateSchema>;
